/**
 * Map layers of a project (phase 2b.6, docs/specs/map.md §4): what each layer draws, in which colour and with which
 * words. The map engine (Leaflet in Field) only turns these features into shapes; the colours are token names read
 * from the CSS variables at runtime (brand/building-teslyn.md, Map conventions). Survey lines, boundaries and line
 * states use map colour tokens of their own (`map-line-*`, decision 352), never the tenant's brand colours: planned
 * lines `map-line-planned` dashed, line states `map-line-unflown` to `map-line-completed` (decision 372), boundaries
 * `map-line-boundary`, landholder properties the 12 `map-property-*` fills (decision 374); aircraft in their tracker
 * source's colour (decision 280). Every colour is paired with a word in the legend and the tooltip (brand QC rule).
 *
 * Decision 654 (docs/proposals/2026-10-26-map-colours.md): every survey line, boundary, plan line and hazard line has a
 * thin dark edge under it (`casing`, `map-line-casing`); planned and unflown lines, line files and coverage follow the
 * base map (`baseMapKind`: the tokens' imagery and light values), the boundary is one blue everywhere (decisions 655,
 * 658); Completed is thin on every map; landholder outlines are dotted and neutral (`map-property-outline`), the colour
 * only in the fill; hazard lines keep their file colours, drawn dash-dot over the edge.
 *
 * Layers: survey blocks and boundaries, planned lines, flown lines by line status (ADR-013), lines remaining (the
 * project reporting spec §4.2.1 design note; `lines-remaining.ts`), hazards, landholder boundaries (Q56), test lines
 * and bases (project positions), KML overlays, NOTAMs (`notam-map.ts`) and aircraft (an interface the tracker stream fills, `aircraft.ts`).
 */
import { formatKm } from './km.js';
import type { Block, LineStatus, PlannedLine, Position, Project } from './model.js';
import {
  kmlColourToCss,
  styleOf,
  type HazardSet,
  type IdentifyRow,
  type KmlDocument,
  type KmlFolder,
  type KmlGeometry,
  type LandParcel,
} from './map-kml.js';
import { landParcelIdentify } from './map-kml.js';
import { kmlDescriptionText } from './kml-text.js';
import type { TenantRole } from './hazards.js';
import { boundsOf, projectedToLatLng, type GeoBounds } from './map-geo.js';
import type { LatLng } from './planning.js';
import { plannedLineRole } from './reflights.js';
import type { AviationColour } from './openaip.js';
import type { HubColour } from './ops-hub.js';
import type { StationColour } from './stations-webcams.js';
import type { BaseLayerId } from './tile-cache.js';
import type { SelectionColour } from './property-layers.js';
import type { FireColour } from './fire-incidents.js';
import type { HotspotColour } from './hotspots.js';
import { isLargeTowerFolder, isTowerFolder, type MapSymbol } from './map-symbols.js';

export type MapLayerId =
  | 'airspace'
  | 'blocks'
  | 'planned'
  | 'lines'
  | 'flown'
  | 'remaining'
  | 'notams'
  | 'hazards'
  | 'landholders'
  | 'osm_boundaries'
  | 'property_identify'
  | 'positions'
  | 'kml'
  | 'photos'
  | 'airports'
  | 'navaids'
  | 'reporting'
  | 'weather_stations'
  | 'webcams'
  | 'stations'
  | 'basemag'
  | 'hotspots'
  | 'fires'
  | 'aircraft';

export interface MapLayerDef {
  readonly id: MapLayerId;
  readonly label: string;
  readonly group: MapLayerGroup;
  readonly defaultOn: boolean;
}

export type MapLayerGroup = 'Survey' | 'Progress' | 'Safety' | 'Aviation' | 'Weather' | 'Reference' | 'Live';

/** The layer panel's groups in order. */
export const MAP_LAYER_GROUPS: readonly MapLayerGroup[] = [
  'Survey',
  'Progress',
  'Safety',
  'Aviation',
  'Weather',
  'Reference',
  'Live',
];

/** The layer list in drawing order (bottom first) and the layer panel's order. */
export const MAP_LAYERS: readonly MapLayerDef[] = [
  // OpenAIP airspace under every survey layer, as the tool's airspace pane (am:5133); docs/specs/map.md §19
  { id: 'airspace', label: 'Airspace (OpenAIP)', group: 'Aviation', defaultOn: false },
  { id: 'blocks', label: 'Survey blocks and boundaries', group: 'Survey', defaultOn: true },
  { id: 'planned', label: 'Planned lines', group: 'Survey', defaultOn: true },
  // .lns files and survey-lines JSON dropped or opened on the map (item 04-054)
  { id: 'lines', label: 'Survey line files', group: 'Survey', defaultOn: true },
  // the five line states from Line review (decision 372)
  { id: 'flown', label: 'Line states', group: 'Progress', defaultOn: true },
  { id: 'remaining', label: 'Lines remaining', group: 'Progress', defaultOn: false },
  { id: 'landholders', label: 'Landholder boundaries', group: 'Reference', defaultOn: true },
  // OpenStreetMap's admin, protected and farm boundaries (Diurnal parity 04-021, `property-layers.ts`): switched on
  // with the state property layers, in their own list
  { id: 'osm_boundaries', label: 'OSM boundaries', group: 'Reference', defaultOn: false },
  // the identify highlight, the Ctrl+click selection and the NT outline being drawn (04-022, 05-020): always shown,
  // never in the layer lists
  { id: 'property_identify', label: 'Property identify', group: 'Reference', defaultOn: true },
  { id: 'positions', label: 'Test lines and bases', group: 'Survey', defaultOn: true },
  { id: 'kml', label: 'KML and shape file overlays', group: 'Reference', defaultOn: true },
  // project photos with a place in their EXIF (decision 429)
  { id: 'photos', label: 'Photos', group: 'Reference', defaultOn: true },
  // NOTAMs from NAIPS (docs/specs/map.md §14), staff only (decision 213), under hazards and aircraft
  { id: 'airports', label: 'Airports (OpenAIP)', group: 'Aviation', defaultOn: false },
  { id: 'navaids', label: 'Navaids (OpenAIP)', group: 'Aviation', defaultOn: false },
  { id: 'reporting', label: 'Reporting points (OpenAIP)', group: 'Aviation', defaultOn: false },
  { id: 'notams', label: 'NOTAMs (NAIPS)', group: 'Safety', defaultOn: true },
  { id: 'hazards', label: 'Hazards', group: 'Safety', defaultOn: true },
  // Weather Underground stations and Windy webcams (docs/specs/map.md §22, decision 469): offered only while the
  // organisation's switch is on (`stations-webcams.ts`); remove these two lines to remove them
  {
    id: 'weather_stations',
    label: 'Weather stations (Weather Underground)',
    group: 'Weather',
    defaultOn: false,
  },
  { id: 'webcams', label: 'Webcams (Windy)', group: 'Weather', defaultOn: false },
  // the magnetic observatories near the survey (decision 528, `stations-layer.ts`); TA Live stations once they
  // report a position
  { id: 'stations', label: 'Stations (observatories)', group: 'Reference', defaultOn: false },
  // where the project's base magnetometers stood, from their files' GPS (decision 250, `basemag-gps.ts`): staff only
  { id: 'basemag', label: 'Basemag stations (staff only)', group: 'Live', defaultOn: true },
  // satellite hot spots from DEA Hotspots (docs/specs/map.md §30, decision 796) under the agencies' fires; off at
  // first, as the fires (decision 477)
  { id: 'hotspots', label: 'Satellite hot spots (DEA)', group: 'Safety', defaultOn: false },
  // fire incidents and warnings from the state agencies' feeds (docs/specs/map.md §29); off at first (decision 477)
  { id: 'fires', label: 'Fires (state agencies)', group: 'Safety', defaultOn: false },
  { id: 'aircraft', label: 'Aircraft', group: 'Live', defaultOn: true },
];

/** Brand colour tokens a map feature may use (CSS variable names without `--`). */
export type MapColour =
  | 'ink'
  | 'ink-muted'
  | 'line-strong'
  | 'pass'
  | 'warn'
  | 'fail'
  | 'accent'
  | 'brand'
  | MapLineColour
  | FlightColour
  | PropertyColour
  | NotamColour
  | AviationColour
  | AircraftColour
  | HubColour
  | StationColour
  | PropertyLayerColour
  | FireColour
  | HotspotColour;

/**
 * The property layers' own colours (Diurnal parity B3, `property-layers.ts`; the tool's am:23766, 25475, 25953,
 * 8586): the identify highlight, the NT outline being drawn, the six Ctrl+click selection colours and the
 * OpenStreetMap boundaries by kind.
 */
export type PropertyLayerColour =
  | 'map-property-highlight'
  | 'map-property-draw'
  | SelectionColour
  | 'map-osm-region'
  | 'map-osm-local'
  | 'map-osm-locality'
  | 'map-osm-protected'
  | 'map-osm-farm';

/**
 * Map colour tokens of their own for survey lines, boundaries and line states (decision 352; `brand/tokens/tokens.json`
 * group `mapLine`). Never set by a tenant. Planned lines, line files and boundaries keep the look they had in Teslyn's
 * own colours; the line states are decision 372's (`line-states.ts`), with Reflight planned (`map-line-reflight`,
 * yellow) and Part accepted (`map-line-part-accepted`, purple) of their own (MB, 25 Oct 2026); `map-line-selected` is the
 * planning selection's glow (decision 654: white over imagery, dark on light maps, under the line's own state colour);
 * `map-line-casing` the dark edge under every line (decision 654); `map-ruler` the ruler (the Diurnal red, am:8473).
 */
export type MapLineColour =
  | 'map-line-planned'
  | 'map-line-file'
  | 'map-line-boundary'
  | 'map-line-coverage'
  | 'map-line-selected'
  | 'map-line-unflown'
  | 'map-line-awaiting-qc'
  | 'map-line-completed'
  | 'map-line-to-fly'
  | 'map-line-rejected'
  | 'map-line-reflight'
  | 'map-line-part-accepted'
  | 'map-line-casing'
  | 'map-ruler';

/** The six fixed flight colours of a plan (decision 654), flight 7 on taking flight 1's again (`plan-map.ts`). */
export type FlightColour = `map-flight-${number}`;

/** The flight colour tokens, flight 1 first. */
export const FLIGHT_COLOURS: readonly FlightColour[] = Array.from(
  { length: 6 },
  (_, i) => `map-flight-${i + 1}` as FlightColour,
);

/** Landholder property colours (decision 374): the Diurnal tool's 12 fills and the flag outlines. */
export type PropertyColour =
  `map-property-${number}` | 'map-property-objection' | 'map-property-notification' | 'map-property-outline';

/** The 12 property fills, in the Diurnal order (`PINNED_COLORS` am:23835). */
export const PROPERTY_PALETTE: readonly PropertyColour[] = Array.from(
  { length: 12 },
  (_, i) => `map-property-${i + 1}` as PropertyColour,
);

/** Every map colour token of its own, in `tokens.json` order (the test keeps the two equal). */
export const MAP_LINE_TOKENS: readonly (MapLineColour | FlightColour | PropertyColour)[] = [
  'map-line-planned',
  'map-line-file',
  'map-line-boundary',
  'map-line-coverage',
  'map-line-selected',
  'map-line-unflown',
  'map-line-awaiting-qc',
  'map-line-completed',
  'map-line-to-fly',
  'map-line-rejected',
  'map-line-reflight',
  'map-line-part-accepted',
  'map-line-casing',
  ...FLIGHT_COLOURS,
  ...PROPERTY_PALETTE,
  'map-property-objection',
  'map-property-notification',
  'map-property-outline',
  'map-ruler',
];

/**
 * The map colour tokens with a value for each kind of base map rather than each app theme (decision 654): the map's
 * box carries `data-basemap` and the CSS gives these their imagery or light value.
 */
export const BASE_MAP_TOKENS: readonly MapColour[] = [
  'map-line-planned',
  'map-line-file',
  'map-line-coverage',
  'map-line-selected',
  'map-line-unflown',
  'map-property-outline',
];

/** What a base map is under the lines: aerial imagery (dark, busy ground) or a light map. */
export type BaseMapKind = 'imagery' | 'light';

/**
 * The kind of a base map (decision 654): Satellite and Satellite with labels (Esri World Imagery) are imagery;
 * Roads (OpenStreetMap), Terrain (OpenTopoMap) and Topographic (Esri Topo) are light. The 3D view is always imagery.
 */
export function baseMapKind(base: BaseLayerId): BaseMapKind {
  return base === 'satellite' || base === 'hybrid' ? 'imagery' : 'light';
}

/**
 * The Diurnal tool's aircraft colours per tracker source (decision 280), kept as tokens (`tracker.ts`
 * `aircraftColours`): the tones of V2Track and Spider Tracks, one colour for each other source, the aeromedical
 * body (decision 284) and the grey of an aircraft with no recent position (decision 326).
 */
export type AircraftColour =
  | `aircraft-v2track-${number}`
  | `aircraft-spidertracks-${number}`
  | 'aircraft-tracplus'
  | 'aircraft-websentinel'
  | 'aircraft-opensky'
  | 'aircraft-adsb'
  | 'aircraft-ozrunways'
  | 'aircraft-avplan'
  | 'aircraft-bushfireio'
  | 'aircraft-aeromedical'
  | 'aircraft-stale';

/** The Diurnal tool's NOTAM category colours, kept as tokens (`notam-map.ts`, docs/specs/map.md §14.4). */
export type NotamColour =
  | 'notam-rpd-r'
  | 'notam-rpd-d'
  | 'notam-rpd-p'
  | 'notam-rpd'
  | 'notam-rwy'
  | 'notam-nav'
  | 'notam-airspace'
  | 'notam-obs'
  | 'notam-gnss'
  | 'notam-other';
export type MapStroke = 'thin' | 'normal' | 'thick';

export interface FeatureStyle {
  /** A brand token, or a data colour from a KML file (CSS `rgba()`, never re-tinted). */
  readonly colour: MapColour | { readonly css: string };
  readonly stroke: MapStroke;
  readonly dashed: boolean;
  /** Polygon fill opacity, 0 for none. */
  readonly fill: number;
  /** The fill's own colour when it differs from the outline (a flagged property, decision 374). */
  readonly fill_colour?: MapColour;
  /** Dotted or dash-dot instead of `dashed` (decision 654). */
  readonly pattern?: MapPattern;
  /** The thin dark edge under the line (`map-line-casing`, decision 654): survey, boundary, plan and hazard lines. */
  readonly casing?: boolean;
  /** Ticked for the plan: a wide glow (`map-line-selected`) under the line, which keeps its state colour (decision 654). */
  readonly glow?: boolean;
  /** Faded (`map-fade-opacity`, decision 654): the line states while the planning map shows a plan. */
  readonly faded?: boolean;
  /** A point drawn as this shape in its colour instead of a dot (`map-symbols.ts`; R143, R149). */
  readonly symbol?: MapSymbol;
  /** The symbol at its large size (the verified towers, as the Diurnal tool drew them). */
  readonly symbol_large?: boolean;
  /** The symbol turned this many degrees clockwise from north: a direction arrow (decision 1011). */
  readonly rotation_deg?: number;
}

/** A line's dash other than solid or `dashed` (decision 654): landholder outlines dotted, hazard lines dash-dot. */
export type MapPattern = 'dotted' | 'dash-dot';

export type MapGeometry =
  | { readonly type: 'point'; readonly at: LatLng }
  | { readonly type: 'line'; readonly points: readonly LatLng[] }
  | { readonly type: 'polygon'; readonly rings: ReadonlyArray<readonly LatLng[]> };

export interface MapFeature {
  /** Unique within the layer; with the layer it is the key of a pinned item (`<layer>:<id>`). */
  readonly id: string;
  readonly layer: MapLayerId;
  readonly geometry: MapGeometry;
  readonly style: FeatureStyle;
  /** Hover text: names the state in words. */
  readonly tooltip: string;
  /** A text label drawn on the map at `label_at` (e.g. "100071 reflight"). */
  readonly label?: string;
  readonly label_at?: LatLng;
  /** The label's edge colour: a plan's "Flight n" tag in the flight's colour (decision 654). */
  readonly label_colour?: MapColour;
  readonly identify: readonly IdentifyRow[];
  /** A page about the item the identify panel can open in the browser (an agency's incident page). */
  readonly link?: { readonly url: string; readonly label: string };
  /**
   * A patch or reflight still to fly (decisions 1011, 1013): the way it is to be flown, degrees from grid north as the
   * Refly list gives it (`plannedDirection`: the way the original was flown, else its planned direction). The line's
   * points run that way; `directionArrowFeatures` draws the arrows along it.
   */
  readonly direction_deg?: number;
  /** Where `direction_deg` comes from: the original's flown record, or its plan (not flown yet). */
  readonly direction_from?: 'flown' | 'planned';
}

/** A legend row: the swatch and the word that always goes with it. */
export interface LegendEntry {
  readonly colour: MapColour;
  readonly stroke: MapStroke;
  readonly dashed: boolean;
  readonly word: string;
  /** Drawn with the selection glow (Ticked for the plan, decision 654). */
  readonly glow?: boolean;
  readonly pattern?: MapPattern;
  /** A point layer drawn as this symbol: the legend shows the shape in the colour, not a swatch. */
  readonly symbol?: MapSymbol;
}

/** Items a layer could not draw, with the reason (shown under the layer, never silent). */
export interface LayerResult {
  readonly features: MapFeature[];
  readonly notDrawn: string[];
}

const style = (
  colour: FeatureStyle['colour'],
  stroke: MapStroke,
  dashed = false,
  fill = 0,
): FeatureStyle => ({
  colour,
  stroke,
  dashed,
  fill,
});

/** A survey line, boundary or plan line: with the thin dark edge under it (decision 654). */
export const edged = (s: FeatureStyle): FeatureStyle => ({ ...s, casing: true });

const km = (m: number): string => `${formatKm(m / 1000)} km`;

/** The EPSG of a block: its own, else the project's. */
export function blockEpsg(
  block: Pick<Block, 'epsg'> | undefined,
  project: Pick<Project, 'epsg'>,
): number | null {
  return block?.epsg ?? project.epsg ?? null;
}

/** A planned line (or a patch) as two WGS 84 points, or null when its projection is unknown. */
export function plannedLinePoints(
  line: PlannedLine,
  blocks: ReadonlyMap<string, Block>,
  project: Pick<Project, 'epsg'>,
): [LatLng, LatLng] | null {
  const epsg = blockEpsg(blocks.get(line.block_id), project);
  const a = projectedToLatLng(line.x1, line.y1, epsg);
  const b = projectedToLatLng(line.x2, line.y2, epsg);
  return a && b ? [a, b] : null;
}

/** The part of a planned line from `fromM` to `toM` metres along it from (x1, y1), as WGS 84 points. */
export function plannedSegmentPoints(
  line: PlannedLine,
  fromM: number,
  toM: number,
  blocks: ReadonlyMap<string, Block>,
  project: Pick<Project, 'epsg'>,
  offsetM = 0,
): [LatLng, LatLng] | null {
  const epsg = blockEpsg(blocks.get(line.block_id), project);
  const len = Math.hypot(line.x2 - line.x1, line.y2 - line.y1);
  if (len === 0) return null;
  const ux = (line.x2 - line.x1) / len;
  const uy = (line.y2 - line.y1) / len;
  const at = (d: number): LatLng | null => {
    const t = Math.min(Math.max(d, 0), len);
    // offset to the right of the direction (x1 → x2) by offsetM metres
    return projectedToLatLng(line.x1 + ux * t + uy * offsetM, line.y1 + uy * t - ux * offsetM, epsg);
  };
  const a = at(Math.min(fromM, toM));
  const b = at(Math.max(fromM, toM));
  return a && b ? [a, b] : null;
}

export const blockMap = (blocks: readonly Block[]): Map<string, Block> =>
  new Map(blocks.map((b) => [b.id, b]));

/** Survey blocks: the boundary polygon of each block with one (`map-line-boundary` outline, no fill). */
/** The block columns a boundary is drawn from. */
export type BlockOutline = Pick<
  Block,
  | 'id'
  | 'name'
  | 'digit'
  | 'epsg'
  | 'boundary'
  | 'planned_km'
  | 'deleted_at'
  | 'trav_spacing_m'
  | 'tie_spacing_m'
  | 'terrain_clearance_m'
>;

export function blockFeatures(blocks: readonly BlockOutline[], project: Pick<Project, 'epsg'>): LayerResult {
  const features: MapFeature[] = [];
  const notDrawn: string[] = [];
  for (const b of blocks.filter((x) => x.deleted_at === null)) {
    if (!b.boundary || b.boundary.length < 3) {
      notDrawn.push(`Block ${b.name}: no boundary recorded`);
      continue;
    }
    const epsg = blockEpsg(b, project);
    const ring = b.boundary.map(([e, n]) => projectedToLatLng(e, n, epsg));
    if (ring.some((p) => p === null)) {
      notDrawn.push(`Block ${b.name}: no map projection (EPSG ${epsg ?? 'not set'})`);
      continue;
    }
    const pts = ring as LatLng[];
    features.push({
      id: b.id,
      layer: 'blocks',
      geometry: { type: 'polygon', rings: [pts] },
      style: edged(style('map-line-boundary', 'normal', false, 0)),
      tooltip: `Block ${b.digit} ${b.name}`,
      label: b.name,
      label_at: pts[0]!,
      identify: [
        { label: 'Block', value: `${b.digit} ${b.name}` },
        { label: 'Planned', value: km(b.planned_km * 1000) },
        ...(b.trav_spacing_m ? [{ label: 'Traverse spacing', value: `${b.trav_spacing_m} m` }] : []),
        ...(b.tie_spacing_m ? [{ label: 'Tie spacing', value: `${b.tie_spacing_m} m` }] : []),
        ...(b.terrain_clearance_m
          ? [{ label: 'Terrain clearance', value: `${b.terrain_clearance_m} m` }]
          : []),
      ],
    });
  }
  return { features, notDrawn };
}

const kindWord = (l: PlannedLine): string => (l.kind === 'tie' ? 'Tie' : 'Traverse');

/**
 * Planned lines: original lines in `map-line-planned`, dashed, thin; unflown patches in `map-line-to-fly` (the part still
 * to fly, decision 372) and reflights in `map-line-reflight` (yellow, as the lines-remaining map; MB, 25 Oct 2026) over
 * their own geometry (a patch only over its segment).
 */
export function plannedLineFeatures(
  plan: readonly PlannedLine[],
  blocks: readonly Block[],
  project: Pick<Project, 'epsg'>,
): LayerResult {
  const bm = blockMap(blocks);
  const features: MapFeature[] = [];
  let missing = 0;
  for (const l of plan.filter((p) => p.deleted_at === null)) {
    const pts = plannedLinePoints(l, bm, project);
    if (!pts) {
      missing += 1;
      continue;
    }
    const role = plannedLineRole(l);
    const word = role === 'original' ? 'Planned' : role === 'patch' ? 'Patch planned' : 'Reflight planned';
    features.push({
      id: l.id,
      layer: 'planned',
      geometry: { type: 'line', points: pts },
      style: edged(
        role === 'original'
          ? style('map-line-planned', 'thin', true)
          : style(role === 'patch' ? 'map-line-to-fly' : 'map-line-reflight', 'normal'),
      ),
      tooltip: `${l.number} · ${kindWord(l)} · ${word} · ${km(l.length_m)}`,
      identify: [
        { label: 'Line', value: String(l.number) },
        { label: 'Kind', value: kindWord(l) },
        { label: 'State', value: word },
        { label: 'Length', value: km(l.length_m) },
        { label: 'Block', value: bm.get(l.block_id)?.name ?? '' },
      ],
    });
  }
  return {
    features,
    notDrawn: missing > 0 ? [`${missing} planned line(s): no map projection for their block`] : [],
  };
}

/**
 * Legends of several layers as one list, each entry once (the Line states layer and the lines-remaining map share
 * four entries, decision 605): the first of entries with the same word, colour, stroke and dash is kept.
 */
export function mergeLegends(...lists: ReadonlyArray<readonly LegendEntry[]>): LegendEntry[] {
  const seen = new Set<string>();
  const out: LegendEntry[] = [];
  for (const e of lists.flat()) {
    const key = [e.word, e.colour, e.stroke, e.dashed].join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

/**
 * The survey block boundary in a legend (decision 731): the boundary blue at the normal width over its dark edge, as
 * `blockFeatures` draws it, on every map that draws the boundaries.
 */
export const BOUNDARY_LEGEND: LegendEntry = {
  colour: 'map-line-boundary',
  stroke: 'normal',
  dashed: false,
  word: 'Survey boundary',
};

/** Words and colours of a line status on the map. */
export const ACCEPTANCE_LEGEND: Readonly<Record<LineStatus['acceptance'], LegendEntry>> = {
  accepted: { colour: 'map-line-completed', stroke: 'thin', dashed: false, word: 'Accepted' },
  partial: { colour: 'map-line-part-accepted', stroke: 'normal', dashed: false, word: 'Part accepted' },
  rejected: { colour: 'map-line-rejected', stroke: 'normal', dashed: false, word: 'Rejected' },
  pending: { colour: 'map-line-awaiting-qc', stroke: 'normal', dashed: false, word: 'Awaiting review' },
};

/**
 * Flown lines from reviewed line status (ADR-013): each status drawn over the planned line it flew (a patch over its
 * segment), coloured by its review. When the log gave where the run started and ended on the line
 * (`start_along_m`/`end_along_m`), only that part is drawn.
 */
export function flownLineFeatures(
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  blocks: readonly Block[],
  project: Pick<Project, 'epsg'>,
): LayerResult {
  const bm = blockMap(blocks);
  const byId = new Map(plan.map((p) => [p.id, p]));
  const byNumber = new Map(plan.filter((p) => p.deleted_at === null).map((p) => [p.number, p]));
  const features: MapFeature[] = [];
  let missing = 0;
  for (const s of statuses.filter((x) => x.deleted_at === null)) {
    const line = byNumber.get(s.line_number) ?? byId.get(s.planned_line_id);
    if (!line) {
      missing += 1;
      continue;
    }
    const original = line.reflight_of_line_id ? (byId.get(line.reflight_of_line_id) ?? line) : line;
    const covered =
      s.start_along_m != null && s.end_along_m != null
        ? plannedSegmentPoints(original, s.start_along_m, s.end_along_m, bm, project)
        : null;
    const pts = covered ?? plannedLinePoints(line, bm, project);
    if (!pts) {
      missing += 1;
      continue;
    }
    const legend = ACCEPTANCE_LEGEND[s.acceptance];
    features.push({
      id: s.id,
      layer: 'flown',
      geometry: { type: 'line', points: pts },
      style: edged(style(legend.colour, legend.stroke, legend.dashed)),
      tooltip: `${s.line_number} · ${legend.word} · flown ${km(s.flown_m)}`,
      identify: [
        { label: 'Line', value: String(s.line_number) },
        { label: 'Review', value: legend.word },
        { label: 'Flown', value: km(s.flown_m) },
        { label: 'Accepted', value: km(s.accepted_m) },
        ...(s.reason ? [{ label: 'Reason', value: s.reason }] : []),
      ],
    });
  }
  return {
    features,
    notDrawn: missing > 0 ? [`${missing} flown line(s): no planned line or map projection`] : [],
  };
}

const POSITION_WORD: Readonly<Record<Position['kind'], string>> = {
  base_mag: 'Base mag',
  ground_cal: 'Ground cal',
  test_line: 'Test line',
};

const en = (e: number, n: number): string => `${e.toFixed(1)} m E, ${n.toFixed(1)} m N`;

/**
 * Test lines and bases: the project's base mag, ground cal and test line positions. A test line with its end (R14:
 * entered in Project settings or taken from a flight log's 801 or 802 run) is drawn as the line from its start to its
 * end (MB's R105, 26 Oct 2026), labelled at its start; without an end it is its start alone, as the bases.
 */
export function positionFeatures(
  positions: readonly Position[],
  project: Pick<Project, 'epsg'>,
): LayerResult {
  const features: MapFeature[] = [];
  const notDrawn: string[] = [];
  for (const p of positions.filter((x) => x.deleted_at === null)) {
    const word = POSITION_WORD[p.kind];
    if (p.easting === null || p.northing === null) {
      notDrawn.push(`${word} ${p.name}: no position recorded (Project settings)`);
      continue;
    }
    const at = projectedToLatLng(p.easting, p.northing, project.epsg);
    if (!at) {
      notDrawn.push(`${word} ${p.name}: no map projection (project EPSG not set)`);
      continue;
    }
    const end =
      p.kind === 'test_line' && p.easting_end != null && p.northing_end != null
        ? projectedToLatLng(p.easting_end, p.northing_end, project.epsg)
        : null;
    if (end && p.easting_end != null && p.northing_end != null) {
      const length = km(Math.hypot(p.easting_end - p.easting, p.northing_end - p.northing));
      features.push({
        id: p.id,
        layer: 'positions',
        geometry: { type: 'line', points: [at, end] },
        style: style('warn', 'thick'),
        tooltip: `${word} · ${p.name} · ${length}`,
        label: `${word}: ${p.name}`,
        label_at: at,
        identify: [
          { label: word, value: p.name },
          { label: 'Start', value: en(p.easting, p.northing) },
          { label: 'End', value: en(p.easting_end, p.northing_end) },
          { label: 'Length', value: length },
        ],
      });
      continue;
    }
    features.push({
      id: p.id,
      layer: 'positions',
      geometry: { type: 'point', at },
      style: style(p.kind === 'test_line' ? 'warn' : 'accent', 'normal', false, 0.8),
      tooltip: `${word} · ${p.name}`,
      label: `${word}: ${p.name}`,
      label_at: at,
      identify: [
        { label: word, value: p.name },
        { label: 'Easting', value: `${p.easting.toFixed(1)} m` },
        { label: 'Northing', value: `${p.northing.toFixed(1)} m` },
      ],
    });
  }
  return { features, notDrawn };
}

/** A KML shape as the map's shapes (a multi-geometry's parts each on its own). */
export function kmlGeometryToMap(g: KmlGeometry): MapGeometry[] {
  switch (g.type) {
    case 'point':
      return [{ type: 'point', at: g.at }];
    case 'line_string':
      return g.points.length >= 2 ? [{ type: 'line', points: g.points }] : [];
    case 'polygon':
      return g.outer.length >= 3 ? [{ type: 'polygon', rings: [g.outer, ...g.holes] }] : [];
    case 'multi':
      return g.parts.flatMap(kmlGeometryToMap);
  }
}

function folderFeatures(
  doc: { readonly styles: KmlDocument['styles'] },
  folder: KmlFolder,
  layer: 'hazards' | 'kml',
  prefix: string,
  path: string,
  out: MapFeature[],
  hidden: ReadonlySet<string>,
): void {
  const here = path ? `${path} / ${folder.name}` : folder.name;
  if (layer === 'hazards' ? !hazardFolderShown(here, folder.visible, hidden) : hidden.has(here)) return;
  folder.placemarks.forEach((p, i) => {
    if (!p.geometry) return;
    const st = styleOf(doc, p);
    const line = kmlColourToCss(st?.line_color);
    const icon = kmlColourToCss(st?.icon_color);
    const parts = kmlGeometryToMap(p.geometry);
    parts.forEach((geometry, j) => {
      const colour = (geometry.type === 'point' ? (icon ?? line) : line) ?? null;
      const look = style(
        colour ? { css: colour } : layer === 'hazards' ? 'fail' : 'ink',
        'normal',
        false,
        geometry.type === 'polygon' ? 0.1 : 0,
      );
      out.push({
        id: `${prefix}${here}#${i}.${j}`,
        layer,
        geometry,
        // a hazard line (powerline, span) keeps its file colour, drawn dash-dot over the dark edge so it never looks
        // like a survey line (decisions 654, 657)
        style:
          layer === 'hazards' && geometry.type === 'line'
            ? { ...look, pattern: 'dash-dot', casing: true }
            : layer === 'hazards' && geometry.type === 'point' && isTowerFolder(here)
              ? // a tower (matched, inferred, unknown, or a NOTAM obstacle of "Naips Towers") is a tower symbol in the
                // file's colour, the verified ones large, as the Diurnal tool drew them (R149)
                { ...look, symbol: 'tower', ...(isLargeTowerFolder(here) ? { symbol_large: true } : {}) }
              : look,
        tooltip: p.name ? `${p.name} · ${folder.name || 'KML'}` : folder.name || 'KML',
        identify: [
          ...(p.name ? [{ label: 'Name', value: p.name }] : []),
          ...(here ? [{ label: 'Folder', value: here }] : []),
          ...(p.description ? [{ label: 'Description', value: kmlDescriptionText(p.description) }] : []),
          ...p.extended_data.map(([k, v]) => ({ label: k, value: v })),
        ],
      });
    });
  });
  for (const sub of folder.folders) folderFeatures(doc, sub, layer, prefix, here, out, hidden);
}

/**
 * Whether a hazard folder is drawn (bug #56): hidden by the Map's tick (`<path>` in `hidden`), shown by it
 * (`show:<path>`), else as its file says (a hazard file hides Poles, as the Diurnal tool's `buildHazardLeafletLayers`).
 */
export function hazardFolderShown(path: string, fileVisible: boolean, hidden: ReadonlySet<string>): boolean {
  if (hidden.has(path)) return false;
  if (hidden.has(`show:${path}`)) return true;
  return fileVisible;
}

/** A folder of a hazard map in the Files list: top folders and their sub-folders, with the path the ticks use. */
export interface HazardFolderRow {
  readonly path: string;
  readonly name: string;
  readonly depth: number;
  readonly fileVisible: boolean;
}

export function hazardFolderTree(set: HazardSet): HazardFolderRow[] {
  const out: HazardFolderRow[] = [];
  for (const f of set.folders) {
    out.push({ path: f.name, name: f.name, depth: 0, fileVisible: f.visible });
    for (const s of f.folders) {
      out.push({ path: `${f.name} / ${s.name}`, name: s.name, depth: 1, fileVisible: s.visible });
    }
  }
  return out;
}

/**
 * Hazards of a hazard map (`*_hazard.kml`, `map_hazard_read`): the folder tree as drawn by the Diurnal tool, with the
 * file's own colours from its shared `styles` (towers and powerlines are colour-coded data), the boundary, and the custom pins. Folder paths in
 * `hidden` are left out (the layer tree's toggles).
 */
export function hazardFeatures(
  set: HazardSet,
  styles: KmlDocument['styles'],
  key: string,
  hidden: ReadonlySet<string> = new Set(),
): LayerResult {
  const out: MapFeature[] = [];
  for (const f of set.folders) folderFeatures({ styles }, f, 'hazards', `${key}:`, '', out, hidden);
  if (set.boundary.length >= 3 && !hidden.has('Boundary')) {
    out.push({
      id: `${key}:boundary`,
      layer: 'hazards',
      geometry: { type: 'polygon', rings: [set.boundary] },
      style: edged(style('map-line-boundary', 'normal', true, 0)),
      tooltip: `Boundary · ${set.bnd_file_name || set.name}`,
      identify: [{ label: 'Boundary', value: set.bnd_file_name || set.name }],
    });
  }
  if (!hidden.has('Other hazards')) {
    for (const pin of set.pins.filter((p) => p.visible)) {
      out.push({
        id: `${key}:pin:${pin.id}`,
        layer: 'hazards',
        geometry: { type: 'point', at: { lat: pin.lat, lng: pin.lng } },
        style: style('fail', 'normal', false, 0.9),
        tooltip: `Hazard pin · ${pin.name}`,
        label: pin.name,
        label_at: { lat: pin.lat, lng: pin.lng },
        identify: [
          { label: 'Hazard', value: pin.name },
          ...(pin.description ? [{ label: 'Description', value: pin.description }] : []),
          { label: 'Position', value: `${pin.lat.toFixed(6)}, ${pin.lng.toFixed(6)}` },
        ],
      });
    }
  }
  return { features: out, notDrawn: [] };
}

/** A loaded KML or KMZ overlay, drawn with its own colours. */
export function kmlFeatures(doc: KmlDocument, key: string): LayerResult {
  const out: MapFeature[] = [];
  folderFeatures(doc, doc.root, 'kml', `${key}:`, '', out, new Set());
  return { features: out, notDrawn: [] };
}

/** One hazard of the tenant's hazard library (decision 173): drawn on every map of every project. */
export interface TenantHazard {
  readonly key: string;
  /** "Beverley SA" */
  readonly name: string;
  /** `uav_operation_site` for the known BVLOS sites. */
  readonly kind: string;
  readonly note: string;
  /** The file as bundled, for the layer list. */
  readonly file: string;
  readonly doc: KmlDocument;
}

/** Words for a library hazard's kind, in the tooltip and identify rows. */
export const TENANT_HAZARD_KIND: Readonly<Record<string, string>> = {
  uav_operation_site: 'Known UAV operation site (BVLOS)',
};

/**
 * The tenant's hazard library on the hazards layer, in each file's own colours (`fail` where the file has none).
 * Every feature names the site and its kind in words; `hiddenKeys` are the sites the person switched off.
 */
export function tenantHazardFeatures(
  library: readonly TenantHazard[],
  hiddenKeys: ReadonlySet<string> = new Set(),
): LayerResult {
  const out: MapFeature[] = [];
  for (const h of library) {
    if (hiddenKeys.has(h.key)) continue;
    const kind = TENANT_HAZARD_KIND[h.kind] ?? h.kind;
    const own: MapFeature[] = [];
    folderFeatures(h.doc, h.doc.root, 'hazards', `tenant:${h.key}:`, '', own, new Set());
    for (const f of own)
      out.push({
        ...f,
        tooltip: `${h.name} · ${kind}`,
        identify: [
          { label: 'Hazard', value: h.name },
          { label: 'Kind', value: kind },
          { label: 'Source', value: 'Tenant hazard library' },
          ...f.identify,
        ],
      });
  }
  return { features: out, notDrawn: [] };
}

/** The box of a geometry's points, or null. */
function geometryBox(g: KmlGeometry): GeoBounds | null {
  const pts: LatLng[] = [];
  const walk = (x: KmlGeometry): void => {
    if (x.type === 'point') pts.push(x.at);
    else if (x.type === 'line_string') pts.push(...x.points);
    else if (x.type === 'polygon') pts.push(...x.outer);
    else x.parts.forEach(walk);
  };
  walk(g);
  return boundsOf(pts);
}

/** Boxes that overlap or come within 0.002° (about 200 m) of each other: neighbours (the Diurnal `_boundsTouch`). */
const boxesTouch = (a: GeoBounds, b: GeoBounds, eps = 0.002): boolean =>
  a.east + eps >= b.west && b.east + eps >= a.west && a.north + eps >= b.south && b.north + eps >= a.south;

/**
 * Fill colours of the landholder properties (decision 374, the Diurnal `_pickPinnedColor` am:23920): in order, each
 * property takes the least-used of the 12 palette colours not used by a neighbour, so properties side by side always
 * differ; when all 12 are taken next to it, the least-used of all.
 */
export function propertyColours(parcels: readonly LandParcel[]): Map<string, PropertyColour> {
  const out = new Map<string, PropertyColour>();
  const usage = new Map<PropertyColour, number>(PROPERTY_PALETTE.map((c) => [c, 0]));
  const placed: Array<{ box: GeoBounds; colour: PropertyColour }> = [];
  const least = (cs: readonly PropertyColour[]): PropertyColour =>
    [...cs].sort((a, b) => usage.get(a)! - usage.get(b)!)[0]!;
  for (const p of parcels) {
    const box = geometryBox(p.geometry);
    const forbidden = new Set(box ? placed.filter((x) => boxesTouch(box, x.box)).map((x) => x.colour) : []);
    const free = PROPERTY_PALETTE.filter((c) => !forbidden.has(c));
    const colour = least(free.length > 0 ? free : PROPERTY_PALETTE);
    usage.set(colour, usage.get(colour)! + 1);
    out.set(p.id, colour);
    if (box) placed.push({ box, colour });
  }
  return out;
}

/**
 * Landholder boundaries (Q56): a light fill (`fillOpacity`, 15 % unless the person set it, 05-017) in the property's palette
 * colour (decision 374, of every boundary, so hiding one never changes another's) and a thin dotted neutral
 * outline (`map-property-outline`, decision 654: the colour only in the fill, so an outline never looks like a line
 * state); the identify rows follow the viewer's roles (contacts, notes and flags internal only). A property with an
 * objection is outlined dotted in `map-property-objection` (red), one needing notification in
 * `map-property-notification` (amber), at the normal width, with a marker at its centre naming the flag in words; the
 * tooltip names the flag too.
 */
export function landholderFeatures(
  parcels: readonly LandParcel[],
  roles: readonly TenantRole[],
  boundariesSharedWithClient: boolean,
  fillOpacity = 0.15,
): LayerResult {
  const out: MapFeature[] = [];
  const markers: MapFeature[] = [];
  const shown = parcels.filter((x) => x.visible);
  // the colours of every boundary, shown or not, so hiding one never changes another's (05-017)
  const fills = propertyColours(parcels);
  for (const p of shown) {
    const rows = landParcelIdentify(p, roles, boundariesSharedWithClient);
    if (rows.length === 0) continue; // a client without shared boundaries sees nothing
    const flags = rows.filter((r) => r.label === 'Flag').map((r) => r.value);
    const fill = fills.get(p.id) ?? PROPERTY_PALETTE[0]!;
    const flag: MapColour | null = flags.includes('Objection')
      ? 'map-property-objection'
      : flags.includes('Needs notification')
        ? 'map-property-notification'
        : null;
    const outline: MapColour = flag ?? 'map-property-outline';
    for (const [j, geometry] of kmlGeometryToMap(p.geometry).entries()) {
      out.push({
        id: `${p.id}.${j}`,
        layer: 'landholders',
        geometry,
        style: {
          ...style(outline, flag ? 'normal' : 'thin', false, fillOpacity),
          fill_colour: fill,
          pattern: 'dotted',
        },
        tooltip: [p.name, ...flags].join(' · '),
        identify: rows,
      });
    }
    const box = flags.length > 0 ? geometryBox(p.geometry) : null;
    if (box) {
      const at = { lat: (box.south + box.north) / 2, lng: (box.west + box.east) / 2 };
      const words = flags.join(' · ');
      markers.push({
        id: `${p.id}.flag`,
        layer: 'landholders',
        geometry: { type: 'point', at },
        style: style(outline, 'normal', false, 0.9),
        tooltip: `${p.name} · ${words}`,
        label: words,
        label_at: at,
        identify: rows,
      });
    }
  }
  return { features: [...out, ...markers], notDrawn: [] };
}

/** All coordinates of a feature (for zooming to it). */
export function featurePoints(f: MapFeature): LatLng[] {
  switch (f.geometry.type) {
    case 'point':
      return [f.geometry.at];
    case 'line':
      return [...f.geometry.points];
    case 'polygon':
      return f.geometry.rings.flat();
  }
}
